import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import React from "react";
import useGoogleAutocomplete from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { useDispatch, useSelector } from "react-redux";

import { SocialHandleInput } from "../../../components/SocialHandleInput";
import { classNames, getRootDomain } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import * as dashboardActions from "../../../redux/reducers/dashboardReducer";
import * as schoolActions from "../../../redux/reducers/schoolReducer";
import * as userActions from "../../../redux/reducers/userReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForUsers } from "../../../utils/confighelpers";
import { handlesFromLinks, linksFromHandles } from "../../../utils/socialLinks";
import { FcConfirmDialog, FieldError, FieldLabel, Icon, inputClass } from "../../classes/schedule/ui";
import { ImagePicker, initialsOf, PrimaryButton, SettingsCard } from "./shared";
import { PAYMENTS_REQUIRED_TEXT, PaymentsRequiredDialog, usePaymentsGate } from "../../../components/PaymentsRequired";
import { ShareLinkBox, TrialBookingCard, trialLinkFor } from "../../classes/members/TrialLinkModal";
import { cleanClubLinkInput, ClubLinkBadge, PLATFORM_SUPPORT_EMAIL, suggestClubLink, useClubLinkCheck } from "../../../utils/clubLink";

/**
 * Club tab (replaces businessProfile/EditBusinessProfile). Same requests as before:
 * - save: PUT users/updateProfile { myBusinessProfile: {...} } — same fields
 * - club link: locked once set (only FitClub support changes it); a club without one picks it once: POST users/verifyBusinessDomain, then PUT users/updateProfile { myBookingLink }
 * Bugs fixed: one Save for the profile (no three buttons saving the same form), a new logo no longer
 * wipes the cover image, the club-link check no longer leaves every button stuck on a spinner,
 * the check waits until typing stops, and an unavailable link can't be saved.
 * Club phone/email are contact details for members, saved only inside myBusinessProfile (no login change,
 * no verification code). The backend only copies them to the account when the account email/phone is empty.
 */

const PIN = (
  <>
    <path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </>
);

/** Social links: the club types only the handle / page name (saved as full links, see utils/socialLinks). Twitter is X. */
const SOCIAL = ["facebook", "instagram", "linkedIn", "twitter", "youtube"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+\d{1,4}[\s-]?[\d\s-]{4,}$/;

const TYPE_PLURAL = { class: "classes", event: "events", service: "services" };

const fromProfile = (p = {}) => ({
  businessName: p.businessName || "",
  about: p.about || "",
  logo: p.logo || "",
  address: p.address && (p.address.address || p.address.description) ? p.address : null,
  locationObj: p.locationObj,
  workNumber: p.workNumber || "",
  workEmail: p.workEmail || "",
  workWebsite: p.workWebsite || "",
  socialLink: handlesFromLinks(p.socialLink || {}), // handles in the form
});

const addressText = (a) => (a ? a.description || a.address || "" : "");

export default function ClubTab() {
  const dispatch = useDispatch();
  const { addSuccessToast, addErrorToast } = useToast();
  const userReduxState = useSelector(userState);
  const user = userReduxState.userData || {};
  const profile = user.myBusinessProfile || {};

  const [form, setForm] = React.useState(() => fromProfile(profile));
  const [base, setBase] = React.useState(() => JSON.stringify(fromProfile(profile)));
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [confirmName, setConfirmName] = React.useState(false);

  React.useEffect(() => {
    dispatch(dashboardActions.setSchoolData(profile)); // same as before, on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh when the page has reloaded the profile and nothing is being edited.
  const dirty = JSON.stringify(form) !== base;
  React.useEffect(() => {
    if (!dirty) {
      const f = fromProfile(profile);
      setForm(f);
      setBase(JSON.stringify(f));
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.myBusinessProfile]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((x) => ({ ...x, [k]: "" }));
  };

  /** PUT users/updateProfile, then refresh the stored profile (same as before). */
  const updateProfile = async (body, okMessage) => {
    const res = await axiosInstance({ method: "put", url: `${getUrlForUsers()}updateProfile`, data: body });
    if (res?.status === 200 && res?.data?.statusCode === 200) {
      const data = res.data.data || {};
      dispatch(userActions.setUserData({ appUrl: userReduxState.userData.appUrl, ...data }));
      if (data.myBusinessProfile) {
        dispatch(schoolActions.setName(data.myBusinessProfile.businessName));
        dispatch(schoolActions.setLogo(data.myBusinessProfile.logo));
        dispatch(schoolActions.setCover(Array.isArray(data.myBusinessProfile.images) ? data.myBusinessProfile.images[0] : undefined));
      }
      dispatch(dashboardActions.setSchoolData(data));
      dispatch(dashboardActions.setCalendarSessionId(-1));
      dispatch(dashboardActions.setCalendarOpen(false));
      addSuccessToast(okMessage);
      return data;
    }
    addErrorToast(res?.data?.message || "Couldn't save. Please try again.");
    return null;
  };

  const validate = () => {
    const e = {};
    if (!form.businessName.trim()) e.businessName = "Please enter your club name.";
    if (!form.about.trim()) e.about = "Please tell members about your club.";
    else if (form.about.trim().length < 5) e.about = "Use at least 5 characters.";
    if (!form.address) e.address = "Search and pick your club address.";
    if (!form.workEmail.trim()) e.workEmail = "Please enter the club email.";
    // Format checks only when changed, so an older stored value never blocks saving other fields.
    else if (form.workEmail.trim() !== (profile.workEmail || "").trim() && !EMAIL_RE.test(form.workEmail.trim())) e.workEmail = "Please enter a valid email address.";
    if (form.workNumber.trim() && form.workNumber.trim() !== (profile.workNumber || "").trim() && !PHONE_RE.test(form.workNumber.trim())) e.workNumber = "Use the format +31 6 12345678.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const doSave = async () => {
    const body = {
      myBusinessProfile: {
        businessName: form.businessName.trim(),
        businessTypes: profile.businessTypes,
        businessProfileType: profile.businessProfileType || "business",
        logo: form.logo,
        images: Array.isArray(profile.images) ? profile.images : [], // keep the cover image as it is
        about: form.about,
        locationObj: form.locationObj,
        typeOfBusiness: "",
        workNumber: form.workNumber.trim(),
        workEmail: form.workEmail.trim(),
        workWebsite: form.workWebsite,
        socialLink: linksFromHandles(form.socialLink, profile.socialLink),
        address: { ...(form.address || {}) },
      },
    };
    setSaving(true);
    try {
      const data = await updateProfile(body, "Club profile saved");
      if (data) {
        const f = fromProfile(data.myBusinessProfile || body.myBusinessProfile);
        setForm(f);
        setBase(JSON.stringify(f));
      }
    } catch (err) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (!validate()) return;
    if (profile.businessName && form.businessName.trim() !== profile.businessName) {
      setConfirmName(true);
      return;
    }
    doSave();
  };


  // Each club-profile section gets the same Save (they all save the whole club profile together).
  const saveFooter = (
      <>
        {dirty && (
          <button
            type="button"
            onClick={() => {
              setForm(JSON.parse(base));
              setErrors({});
            }}
            className="rounded-xl px-3 py-2 text-sm font-extrabold text-gray-600 hover:bg-gray-50"
          >
            Discard changes
          </button>
        )}
        <PrimaryButton busy={saving} disabled={!dirty} onClick={save}>
          Save club profile
        </PrimaryButton>
      </>
  );
  return (
    <div className="flex flex-col gap-5">
      <SettingsCard title="Club details" subtitle="Members see these in the FitClub app and on your club page." footer={saveFooter}>
        <div className="mt-5">
          <ImagePicker
            value={form.logo}
            rounded="2xl"
            fallback={<span className="flex h-full w-full items-center justify-center bg-portland-orange text-2xl font-black text-white">{initialsOf(form.businessName, "FC")}</span>}
            buttonLabel={form.logo ? "Change logo" : "Upload logo"}
            help="Optional. Square works best. JPG, PNG, GIF or WebP, up to 1 MB."
            onUploaded={async (url) => set("logo", url)}
            onError={addErrorToast}
          />
        </div>
        <div className="mt-6 flex flex-col gap-4">
          <div>
            <FieldLabel htmlFor="club-name">Club name</FieldLabel>
            <input id="club-name" type="text" value={form.businessName} onChange={(e) => set("businessName", e.target.value)} className={inputClass(errors.businessName)} />
            <FieldError>{errors.businessName}</FieldError>
            {!errors.businessName && <p className="mt-1.5 text-xs text-gray-500">Changing the name changes it everywhere members see it.</p>}
          </div>
          <div>
            <FieldLabel htmlFor="club-about">About your club</FieldLabel>
            <textarea id="club-about" rows={4} value={form.about} onChange={(e) => set("about", e.target.value)} className={classNames(inputClass(errors.about), "h-auto py-3 leading-relaxed")} />
            <FieldError>{errors.about}</FieldError>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Contact" subtitle="Where members can find and reach you." footer={saveFooter}>
        <div className="mt-5">
          <FieldLabel htmlFor="club-address">Club address</FieldLabel>
          <AddressSearch
            id="club-address"
            value={form.address}
            error={errors.address}
            onPick={(addr) => {
              setForm((f) => ({ ...f, address: addr, locationObj: addr }));
              setErrors((x) => ({ ...x, address: "" }));
            }}
          />
          <FieldError>{errors.address}</FieldError>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor="club-phone" optional>
              Club phone
            </FieldLabel>
            <input id="club-phone" type="tel" placeholder="+31 6 12345678" value={form.workNumber} onChange={(e) => set("workNumber", e.target.value)} className={inputClass(errors.workNumber)} />
            <FieldError>{errors.workNumber}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="club-email">Club email</FieldLabel>
            <input id="club-email" type="email" placeholder="hello@yourclub.nl" value={form.workEmail} onChange={(e) => set("workEmail", e.target.value)} className={inputClass(errors.workEmail)} />
            <FieldError>{errors.workEmail}</FieldError>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">Shown to members. Changing these doesn't change how you log in.</p>
      </SettingsCard>

      <SettingsCard
        title="Website & social"
        subtitle="Optional. Shown on your club page so members can follow you."
        footer={saveFooter}
      >
        <div className="mt-5 flex flex-col gap-3">
          <div>
            <FieldLabel htmlFor="club-web" optional>
              Website
            </FieldLabel>
            <input id="club-web" type="text" placeholder="yourclub.nl" value={form.workWebsite} onChange={(e) => set("workWebsite", e.target.value)} className={inputClass(false)} />
          </div>
          {SOCIAL.map((key) => (
            <SocialHandleInput
              key={key}
              platform={key}
              value={form.socialLink[key]}
              onChange={(handle) => set("socialLink", { ...form.socialLink, [key]: handle })}
            />
          ))}
        </div>
      </SettingsCard>

      <ClubLinkCard current={user.myBookingLink || ""} clubName={profile.businessName} updateProfile={updateProfile} />

      <TrialBookingCard />

      <FcConfirmDialog
        open={confirmName}
        title="Change your club name?"
        message={`Members will see the new name on all your ${TYPE_PLURAL[userReduxState.businessType] || "classes"}, bookings and messages. Some may not recognise your club at first.`}
        confirmLabel="Change name"
        cancelLabel="Keep old name"
        busy={saving}
        onClose={() => setConfirmName(false)}
        onConfirm={async () => {
          setConfirmName(false);
          await doSave();
        }}
      />
    </div>
  );
}

/** Google address search; returns the same address object the old TwLocationInput produced. */
function AddressSearch({ id, value, error, onPick }) {
  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } = useGoogleAutocomplete({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API,
  });
  const [query, setQuery] = React.useState("");
  const [editing, setEditing] = React.useState(!value);
  const [resolving, setResolving] = React.useState(false);

  const pick = (prediction) => {
    if (!prediction?.place_id || !placesService) return;
    setResolving(true);
    placesService.getDetails({ placeId: prediction.place_id }, (details) => {
      setResolving(false);
      if (!details?.geometry?.location) return;
      const comps = Array.isArray(details.address_components) ? details.address_components : [];
      const last = comps[comps.length - 1];
      const city = comps[comps.length - 3]?.long_name || "";
      onPick({
        addressType: "current",
        address: prediction.description,
        description: prediction.description,
        houseNumber: prediction.structured_formatting?.main_text || "",
        city,
        zip: last && Array.isArray(last.types) && last.types.find((t) => t.startsWith("postal")) ? last.long_name || "" : "",
        addressName: city ? city : "Business Address",
        location: { type: "Point", coordinates: [details.geometry.location.lat(), details.geometry.location.lng()] },
      });
      setEditing(false);
      setQuery("");
    });
  };

  if (value && !editing) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-[#FBF9FC] p-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFE8EB] text-portland-orange">
          <Icon className="h-5 w-5" d={PIN} />
        </span>
        <span className="min-w-0 flex-1 text-sm font-bold text-gray-900">{addressText(value)}</span>
        <button type="button" onClick={() => setEditing(true)} className="shrink-0 rounded-xl px-3 py-2 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
          Change
        </button>
      </div>
    );
  }

  return (
    <Combobox value={null} onChange={pick}>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400">
          <Icon className="h-5 w-5" d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />
        </span>
        <ComboboxInput
          id={id}
          autoComplete="off"
          placeholder="Search street, venue or postcode"
          className={classNames(inputClass(error), "pl-11")}
          displayValue={() => query}
          onChange={(e) => {
            setQuery(e.target.value);
            getPlacePredictions({ input: e.target.value });
          }}
        />
        {(resolving || isPlacePredictionsLoading) && (
          <span className="absolute inset-y-0 right-4 flex items-center">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-bz" />
          </span>
        )}
      </div>
      {query.trim() !== "" && (
        <ComboboxOptions anchor="bottom start" className="z-[70] mt-1 max-h-64 w-[var(--input-width)] overflow-auto rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl empty:invisible focus:outline-none">
          {placePredictions.map((p) => (
            <ComboboxOption key={p.place_id} value={p} className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 data-[focus]:bg-[#F7F4F8]">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#FFE8EB] text-portland-orange">
                <Icon className="h-4 w-4" d={PIN} />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-bold text-gray-900">{p.structured_formatting?.main_text || p.description}</span>
                <span className="block truncate text-xs text-gray-500">{p.structured_formatting?.secondary_text || ""}</span>
              </span>
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      )}
      {value && (
        <button type="button" onClick={() => setEditing(false)} className="mt-2 text-sm font-extrabold text-gray-600 hover:text-gray-900">
          Keep {addressText(value)}
        </button>
      )}
    </Combobox>
  );
}

/**
 * Club link (Account settings > Club), same look as the Free trial class card:
 *  - the sign-up link (<club>.<root>/members/signup) with Copy link, Share on WhatsApp, Copy text and Open page,
 *    all behind the payments check (members become customers in the club's payment account);
 *  - the club address itself (<myBookingLink>.<root>), locked: a club picks it once (onboarding) and only FitClub
 *    support changes it, on request by email (backend scripts/change-club-link.js; old links keep redirecting).
 *    A club that has no link yet (older accounts) can pick one here, once.
 * Rules and the server check: utils/clubLink.js (backend users/utils.js).
 */
function ClubLinkCard({ current, clubName, updateProfile }) {
  const { addErrorToast } = useToast();
  const [value, setValue] = React.useState(() => (current ? "" : suggestClubLink(clubName)));
  const [saving, setSaving] = React.useState(false);
  const [confirm, setConfirm] = React.useState(false);
  const check = useClubLinkCheck(current ? "" : value);

  const root = getRootDomain();
  const payments = usePaymentsGate();
  const signupUrl = current ? trialLinkFor({ myBookingLink: current }) : "";
  const shareText = `Join ${clubName || "our club"} and book your classes here: ${signupUrl}`;
  const rebrandMail = `mailto:${PLATFORM_SUPPORT_EMAIL}?subject=${encodeURIComponent(`Change club link: ${current}.${root}`)}&body=${encodeURIComponent(
    `Hi FitClub,\n\nWe'd like to change our club link.\n\nClub: ${clubName || ""}\nCurrent link: ${current}.${root}\nNew link we'd like: \nReason (e.g. new club name): \n\nThanks!`
  )}`;

  const save = async () => {
    setConfirm(false);
    if (check.state !== "ok") return;
    setSaving(true);
    try {
      await updateProfile({ myBookingLink: check.link }, "Club link saved");
    } catch (err) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard title="Club link" subtitle="Share it so new members can sign up and book your classes.">
      {current ? (
        <>
          <div className="mt-5 flex flex-col gap-2">
            <div className="text-sm font-extrabold text-gray-900">Your sign-up link</div>
            <ShareLinkBox link={signupUrl} text={shareText} guard={(go) => payments.whenReady(go)} />
            {payments.ready ? (
              <p className="text-xs text-gray-500">Put it on your website, Instagram bio or a QR code on a flyer. Everyone who signs up shows up in My Members.</p>
            ) : (
              <p className="rounded-xl bg-[#FFF7E8] px-3 py-2 text-xs font-semibold text-[#5C3A00]">
                Set up payments before you share this link. Your members buy passes and subscriptions through your club's payment account.
              </p>
            )}
          </div>
          <div className="mt-6 border-t border-gray-100 pt-5">
            <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-[#FBF9FC] p-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F3E9FF] text-indigo-bz">
                <Icon className="h-5 w-5" d={<><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-bold text-gray-500">Club address</span>
                <span className="block truncate text-sm font-bold text-gray-900">
                  {current}.{root}
                </span>
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Your club address is fixed, so the links, QR codes and emails your members have keep working. Rebranding?{" "}
              <a href={rebrandMail} className="font-extrabold text-indigo-bz hover:underline">
                Email FitClub support
              </a>{" "}
              and we'll move it for you. Your old links will keep working.
            </p>
          </div>
        </>
      ) : (
        <div className="mt-5">
          <FieldLabel htmlFor="club-link">Club address</FieldLabel>
          <div
            className={classNames(
              "flex h-12 items-center gap-1 rounded-2xl border bg-[#FBF9FC] px-4 focus-within:bg-white focus-within:ring-2 focus-within:ring-indigo-bz",
              check.state === "taken" || check.state === "invalid" ? "border-red-300" : "border-gray-200"
            )}
          >
            <input
              id="club-link"
              type="text"
              value={value}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => setValue(cleanClubLinkInput(e.target.value))}
              placeholder="yourclub"
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] font-bold text-gray-900 focus:outline-none focus:ring-0"
            />
            <span className="shrink-0 text-[15px] text-gray-500">.{root}</span>
            <span className="ml-2 shrink-0">
              <ClubLinkBadge state={check.state} />
            </span>
          </div>
          {check.state === "taken" || check.state === "invalid" || check.state === "error" ? (
            <FieldError>{check.message}</FieldError>
          ) : (
            <p className="mt-1.5 text-xs text-gray-500">Letters, numbers and dashes, 3–30 characters. Choose carefully: you can't change it yourself later.</p>
          )}
          <div className="mt-4 flex justify-end">
            <PrimaryButton busy={saving} disabled={check.state !== "ok"} onClick={() => setConfirm(true)}>
              Save link
            </PrimaryButton>
          </div>
        </div>
      )}

      <FcConfirmDialog
        open={confirm}
        title="Save your club link?"
        message={`Your club will be at ${check.link}.${root}. You can't change it yourself later, so check the spelling.`}
        confirmLabel="Save link"
        cancelLabel="Not yet"
        busy={saving}
        onClose={() => setConfirm(false)}
        onConfirm={save}
      />
      <PaymentsRequiredDialog {...payments.dialog} message={PAYMENTS_REQUIRED_TEXT.link} />
    </SettingsCard>
  );
}
